import { determineDataSource } from "../../../Data/DataStores/DataSource";
import { InvalidTemplateValueError, StringParser } from "../../Platform/StringParser";

type BarcodeTemplateTypes = ["quantity", "dollar", "cent", "id", "wildcard"];

type BarcodeTemplatePart = {
    type: "literal";
    value: string; // is the literal type is literal, otherwise is the length
} | {
    type: BarcodeTemplateTypes[number];
    value: number;
};

const templateCache = new Map();

export class BarcodeTemplate {
    public static parseTemplate(template: string): Array<BarcodeTemplatePart> {
        if(templateCache.has(template)) {
            return templateCache.get(template);
        }

        const parts = StringParser.parseTemplate<BarcodeTemplateTypes>(template, [{
            value: "$",
            type : "dollar",
        }, {
            value: "C",
            type : "cent",
        }, {
            value: "Q",
            type : "quantity",
        }, {
            value: "X",
            type : "id",
        }, {
            value: "?",
            type : "wildcard",
        }]);

        templateCache.set(template, parts);

        return parts;
    }

    public static createFromTemplate(
        template: string,
        code: string,
        quantity: number,
        dollars: number,
        cents: number
    ): string {
        const parts = BarcodeTemplate.parseTemplate(template);
        const barcode = [];

        let strDollars = Math.round(dollars).toString();
        let strCents = Math.round(cents).toString();
        let strQuantity = Math.round(quantity).toString();

        let codeLength = 0;
        let quantityLength = 0;
        let dollarLength = 0;
        let centLength = 0;

        for(let i = 0, l = parts.length; i < l; i++) {
            const part = parts[i];

            switch(part.type) {
                case "dollar":
                    dollarLength += part.value;
                    break;
                case "cent":
                    centLength += part.value;
                    break;
                case "quantity":
                    quantityLength += part.value;
                    break;
                case "id":
                    codeLength += part.value;
                    break;
            }
        }

        code = code.padStart(codeLength, "0").slice(codeLength * -1);
        strDollars = strDollars.padStart(dollarLength, "0").slice(dollarLength * -1);
        strQuantity = strQuantity.padStart(quantityLength, "0").slice(quantityLength * -1);
        strCents = strCents.padEnd(centLength, "0").slice(0, centLength);

        for(let i = 0, l = parts.length; i < l; i++) {
            const part = parts[i];

            switch(part.type) {
                case "dollar":
                    barcode.push(strDollars.slice(0, part.value));
                    strDollars = strDollars.slice(part.value);
                    break;
                case "cent":
                    barcode.push(strCents.slice(0, part.value));
                    strCents = strCents.slice(part.value);
                    break;
                case "quantity":
                    barcode.push(strQuantity.slice(0, part.value));
                    strQuantity = strQuantity.slice(part.value);
                    break;
                case "id":
                    barcode.push(code.slice(0, part.value));
                    code = code.slice(part.value);
                    break;
                case "wildcard":
                    barcode.push("?".repeat(part.value));
                    break;
                case "literal":
                    barcode.push(part.value);
                    break;
            }
        }

        return barcode.join("");
    }

    /**
     * Gets the barcode templates that match the provided barcode
     */
    public static async getMatchingTemplates(barcode: string): Promise<Array<BarcodeTemplate>> {
        const source = await determineDataSource();
        const templates = await source.barcodeTemplates.all();

        const matching = [];

        for(let i = 0, l = templates.length; i < l; i++) {
            if(templates[i].template.length !== barcode.length) {
                continue;
            }

            let bt;

            try {
                bt = new BarcodeTemplate(
                    barcode,
                    templates[i].template,
                    templates[i].name,
                    templates[i].recalculate_quantity
                );
            } catch(e) {
                if(e instanceof InvalidTemplateValueError) {
                    continue;
                }

                throw e;
            }

            if(!bt.id) {
                continue;
            }

            if(!bt.quantity && !bt.price) {
                continue;
            }

            matching.push(bt);
        }

        return matching;
    }

    public id: null | string = null;
    public quantity: null | number = null;
    public price: null | number = null;

    constructor(
        public readonly barcode: string,
        template: string,
        public readonly name: string,
        public readonly recalculateQuantity: boolean
    ) {
        this.fromTemplate(template);
    }

    public fromTemplate(template: string): void {
        let rawId: string | null = "";
        let strQuantity = "";
        let strDollars = "";
        let strCents = "";

        const parts = StringParser.fromParts(
            this.barcode,
            BarcodeTemplate.parseTemplate(template)
        );

        for(let i = 0, l = parts.length; i < l; i++) {
            const part = parts[i];

            if(part.type === "literal" || part.type === "wildcard") {
                continue;
            }

            switch(part.type) {
                case "dollar":
                    strDollars += part.value;
                    break;
                case "cent":
                    strCents += part.value;
                    break;
                case "quantity":
                    strQuantity += part.value;
                    break;
                case "id":
                    rawId += part.value;
                    break;
            }
        }

        if(rawId === "") {
            rawId = null;
        }

        let rawQuantity: number | null = parseInt(strQuantity);

        if(isNaN(rawQuantity)) {
            rawQuantity = null;
        }

        let rawDollars: number | null = parseInt(strDollars);

        if(isNaN(rawDollars)) {
            rawDollars = null;
        }

        const rawCentsLength = strCents.length;
        let rawCents: number | null = parseInt(strCents);

        if(isNaN(rawCents)) {
            rawCents = null;
        }

        let price = null;

        if(rawDollars !== null) {
            price = `${rawDollars}`;

            if(rawCents !== null) {
                price = `${price}.${rawCents.toString().padStart(rawCentsLength, "0")}`;
            }
        } else if(rawCents !== null) {
            price = `0.${rawCents.toString().padStart(rawCentsLength, "0")}`;
        }

        if(price !== null) {
            price = parseFloat(price);

            if(isNaN(price)) {
                price = null;
            }
        }

        this.id = rawId;
        this.quantity = rawQuantity;
        this.price = price;
    }
}
